import { useState, useEffect, useCallback } from "react";
import { getJournals, getPaperTypes, uploadPaper, } from "../api/Api";
import { showToast } from "../utils/toastUtils";
import { useNavigate } from "react-router-dom";

/**
 * Custom hook for handling paper upload functionality with caching mechanisms
 * for journals and paper types to optimize performance and reduce API calls.
 * 
 * The hook implements two separate caching strategies:
 * 1. Journal Cache: Caches journal search results based on search terms
 * 2. Paper Type Cache: Caches paper types for each journal to avoid redundant API calls
 */
const useUpload = (initiativeId) => {
  const navigate = useNavigate();
  const [uploadData, setUploadData] = useState(null);
  const [paper, setPaper] = useState(null);
  const [journals, setJournals] = useState([]);
  const [paperTypes, setPaperTypes] = useState([]);
  const [isLoadingJournals, setIsLoadingJournals] = useState(false);
  
  // Cache for storing journal search results
  // Key: lowercase search term, Value: array of journal results
  const [journalCache, setJournalCache] = useState(new Map());
  
  const [isLoadingPaperTypes, setIsLoadingPaperTypes] = useState(false);
  
  // Cache for storing paper types per journal
  // Key: journal ID, Value: array of paper types
  const [paperTypeCache, setPaperTypeCache] = useState(new Map());

  /**
   * Loads journals with caching mechanism to optimize performance
   * @param {string} searchTerm - The search term to filter journals
   * 
   * Cache Strategy:
   * - Uses a Map to store search results
   * - Cache key is the lowercase search term
   * - Cache is checked before making API call
   * - Results are cached after successful API response
   * - Cache persists during component lifecycle
   */
  const loadJournals = useCallback(async (searchTerm = "") => {
    try {
      setIsLoadingJournals(true);
      
      // Check cache first using lowercase search term as key
      const cacheKey = searchTerm.toLowerCase();
      if (journalCache.has(cacheKey)) {
        setJournals(journalCache.get(cacheKey));
        return;
      }

      const response = await getJournals(searchTerm);
      
      if (response.data) {
        setJournals(response.data);
        // Cache the results for future use
        setJournalCache(prev => new Map(prev).set(cacheKey, response.data));
      }
    } catch (error) {
      console.error("Failed to load journals:", error);
      showToast("Failed to load journals. Please try again.", "error");
      setJournals([]);
    } finally {
      setIsLoadingJournals(false);
    }
  }, [journalCache]);

  /**
   * Loads paper types for a specific journal with caching mechanism
   * @param {string|number} journalId - The ID of the journal to fetch paper types for
   * 
   * Cache Strategy:
   * - Uses a Map to store paper types per journal
   * - Cache key is the journal ID
   * - Cache is checked before making API call
   * - Results are cached after successful API response
   * - Cache persists during component lifecycle
   * - Cache is cleared when switching journals
   */
  const loadPaperTypes = useCallback(async (journalId) => {
    // Reset paper types immediately when loading new ones
    setPaperTypes([]);
    
    // Don't proceed if no journal ID is provided
    if (!journalId) {
      return;
    }

    try {
      setIsLoadingPaperTypes(true);
      
      // Check cache first using journal ID as key
      if (paperTypeCache.has(journalId)) {
        setPaperTypes(paperTypeCache.get(journalId));
        return;
      }

      const response = await getPaperTypes(journalId);
      
      if (response.data) {
        setPaperTypes(response.data);
        // Cache the results for future use
        setPaperTypeCache(prev => new Map(prev).set(journalId, response.data));
      }
    } catch (error) {
      console.error("Error fetching paper types:", error);
      setPaperTypes([]);
    } finally {
      setIsLoadingPaperTypes(false);
    }
  }, [paperTypeCache]);

  // Handle upload--------------------------------------------
  const handleUpload = async (values) => {
    if (!uploadData) {
      showToast("Please upload a file before proceeding.", "error");
      return;
    }

    

    const payload = {
      name: uploadData.file_name,
      description: "Description of the uploaded paper.",
      initiative_id: initiativeId,
      file_id: uploadData.file_hash,
      journal_id: values.journal_id,
      paper_type_id: values.paper_type_id,
      auto_delete_file: values.auto_delete_file,
    };

    // Check if the selected journal is Lancet to navigate to the preview page
    const selectedJournal = journals.find(
      (journal) => journal.id === Number(values.journal_id)
    );
    const journalName = selectedJournal ? selectedJournal.name : null;

    uploadPaper(payload)
      .then((response) => {
        if (response.status === 201) {
          const paper = response.data;
          setPaper(paper);
          // upload=${journalName} is just a indicator to show this url is coming from upload page
          navigate(
            `/journals/${values.journal_id}/preview/${paper.id}?upload=${journalName}`
          );
        } else {
          showToast(response.data.message, "error");
          console.error("Error uploading data:", response);
        }
      })
      .catch((error) => {
        console.error("Upload failed:", error);
      });
  };

  useEffect(() => {
    loadJournals();
  }, [paper?.id]);

  const clearPaperTypes = () => setPaperTypes([]);

  return {
    uploadData,
    setUploadData,
    journals,
    isLoadingJournals,
    paperTypes,
    isLoadingPaperTypes,
    loadPaperTypes,
    handleUpload,
    loadJournals,
    clearPaperTypes,
  };
};

export default useUpload;
